import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft, Settings as SettingsIcon } from "lucide-react"
import type React from "react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Switch } from "../components/ui/switch"
import { useCurrentUserContext } from "../context/useCurrentUserContext"
import { useSettings, useUpdateSettings } from "../hooks/useSettings"
import { type TTheme, useThemeStore } from "../store"
import { apiFetch } from "../utils/apiFetch"

export const Route = createFileRoute("/_app/settings")({
  component: Settings,
})

export default function Settings() {
  const queryClient = useQueryClient()
  const { user } = useCurrentUserContext()
  const { theme, setTheme } = useThemeStore((state) => state)
  const { data: settings } = useSettings()
  const { mutate: saveSettings } = useUpdateSettings()
  const [name, setName] = useState<string>(user?.name ?? "")

  useEffect(() => {
    if (settings?.theme && settings.theme !== theme) {
      setTheme(settings.theme as TTheme)
    }
  }, [settings?.theme])

  const { mutate: updateName, isPending } = useMutation({
    mutationFn: (newName: string) =>
      apiFetch("/v1/users/display-name", {
        method: "PATCH",
        body: JSON.stringify({ name: newName }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["User"] })
      toast.success("Display name updated")
    },
    onError: () => {
      toast.error("Failed to update display name")
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (name.trim() && name !== user?.name) {
      updateName(name.trim())
    }
  }

  return (
    <div className="container mx-auto max-w-6xl p-4 pb-20">
      <div className="mb-6 flex items-center">
        <Link to="/workout" className="mr-4">
          <Button variant="outline" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="flex items-center font-bold text-2xl">
          <SettingsIcon className="mr-2" /> Account Settings
        </h1>
      </div>

      <Card className="mx-auto max-w-md">
        <CardHeader>
          <CardTitle>Profile Settings</CardTitle>
          <CardDescription>Update your account preferences</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={user.email || ""} disabled className="bg-muted" />
              <p className="text-muted-foreground text-sm">Email cannot be changed</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>
            <Label htmlFor="theme">Theme</Label>
            <Select
              value={theme}
              onValueChange={(value: TTheme) => {
                setTheme(value)
                saveSettings({ theme: value })
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select a theme" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="light">light</SelectItem>
                  <SelectItem value="dark">dark</SelectItem>
                  <SelectItem value="system">system</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={!name.trim() || name === user?.name || isPending}>
              {isPending ? "Saving..." : "Save Changes"}
            </Button>
          </CardFooter>
        </form>
      </Card>

      <Card className="mx-auto mt-6 max-w-md">
        <CardHeader>
          <CardTitle>Workout Settings</CardTitle>
          <CardDescription>Customize your workout experience</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="plate-calculator">Plate Calculator</Label>
              <p className="text-muted-foreground text-sm">Show plate breakdown during workout entry</p>
            </div>
            <Switch
              id="plate-calculator"
              key={`plate-calc-${settings?.showPlateCalculator}`}
              defaultChecked={settings?.showPlateCalculator !== false}
              onCheckedChange={(checked) => saveSettings({ showPlateCalculator: checked })}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
